/**
 * The subset of `DataTableState` that `useGridDefaults` needs.
 *
 * Only `reactTableConfig` is required — the hook mutates the
 * configuration properties directly on the live table state object.
 */
interface GridDefaultTableState {
  reactTableConfig: {
    showGlobalFilter: boolean;
    showTableSettings: boolean;
    showFilterDataset: boolean;
    showDownloadButton: boolean;
    showFullscreenButton: boolean;
    showColumnVisibilityConfig: boolean;
    showPagination: boolean;
    showTopPagination: boolean;
    showBottomPagination: boolean;
    showColumnSort: boolean;
    paginationBottomCompact: boolean;
  };
}

/**
 * Options accepted by `useGridDefaults`.
 */
export interface UseGridDefaultsOptions {
  /** The `tableState` object returned by `useDataTable`. */
  tableState: GridDefaultTableState;
}

/**
 * React hook that applies default configuration values to a DataTable's
 * live `tableState` object.
 *
 * Call this immediately after `useDataTable` — the hook sets the desired
 * default properties on `tableState.reactTableConfig` **synchronously
 * during render** so the DataTable never sees the original values.
 *
 * The mutation is guarded by a ref so that it only runs **once**, even
 * if the component re-renders many times.  This avoids overwriting
 * values that the user or other hooks may have changed after the
 * initial setup.
 *
 * This hook has **no return value**; it mutates `tableState` in place.
 *
 * @example
 * ```tsx
 * const tableState = useDataTable({
 *   initialColumns: columns,
 *   initialData: data,
 * });
 *
 * useGridDefaults({ tableState });
 * ```
 */
export function useGridDefaults({ tableState }: UseGridDefaultsOptions): void {
  if (!tableState?.reactTableConfig) return;

  tableState.reactTableConfig.showGlobalFilter = false;
  tableState.reactTableConfig.showTableSettings = false;
  tableState.reactTableConfig.showDownloadButton = true;
  tableState.reactTableConfig.showPagination = true;
  tableState.reactTableConfig.showBottomPagination = false;
}
